// Built-in (no-dialog) reports from the Tools > Reports menu:
// * Controller Values (C++ OnApp_controller_report_button): save prompt →
//   client-side snapshot document → offer to open.
// * Event-model reports (C++ event_menu_item_cls::check_and_perform): save
//   prompt → progress dialog while the alarm server replies → offer to open.
// Plus Window > Save Tools as Report (C++ Ui_tool_win_save): the open tool
// windows captured into a report or a .xml/.fodt report template.
// The localized strings baked into the documents are resolved here (the
// backend has no resource table) and passed to the generator commands.

import { getTigerResource, tigerFormat } from "./tigerResource";
import { collectToolSetDialogs } from "./toolSetSnapshot";
import { PickerKey, pickerSavePath, rememberPickerDir } from "./pickerFolder";
import { downloadReport } from "./downloadReport";
import { promptReportSave } from "./reportSavePrompt";
import {
  CONTROLLER_REPORT_FORMATS,
  EVENT_REPORT_FORMATS,
  STABILITY_REPORT_FORMATS,
  TOOL_REPORT_FORMATS,
  toTauriFilters,
} from "./reportFormats";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** The C++ footer keeps TIGER_CONTROL_PAGE up to its `%d` and appends the live
 *  PAGE field after it. */
export function controllerPageLabel(): string {
  const raw = getTigerResource("TIGER_CONTROL_PAGE");
  const i = raw.indexOf("%d");
  return i >= 0 ? raw.slice(0, i) : raw;
}

/** Success prompt + open-with-associated-app offer shared by both reports. */
async function offerToOpen(outputFile: string, title: string): Promise<void> {
  const { ask } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");
  const openIt = await ask(
    tigerFormat(getTigerResource("TIGER_REPORT_SUCCESS_MSG"), outputFile),
    { title, kind: "info" },
  );
  if (openIt) {
    await invoke("open_report_file", { path: outputFile }).catch((e) =>
      console.error("open_report_file failed:", e),
    );
  }
}

/** Controller Values report: a snapshot of every analog/digital channel.
 *  .pdf is the flat-ODF document run through `convert_file` (the C++ draws its
 *  own PDF with libTpdf instead). */
export async function generateControllerReport(
  configName: string,
  onBusy?: (busy: boolean) => void,
): Promise<void> {
  if (!isTauri()) {
    // No native save dialog in a browser: ask for the name + format, then let
    // the webserver generate that extension and stream it back.
    const filename = await promptReportSave({
      defaultName: `${configName}_tiger_controller_report.doc`,
      formats: CONTROLLER_REPORT_FORMATS,
      rememberKey: PickerKey.controllerReport,
    });
    if (!filename) return; // cancelled
    const params = new URLSearchParams({
      config_name: configName,
      filename,
      title_format: getTigerResource("TIGER_CONTROL_TITLE"),
      page_label: controllerPageLabel(),
    });
    onBusy?.(true);
    try {
      const { warning } = await downloadReport(
        `/api/create_controller_report?${params}`,
        filename,
      );
      // A .pdf/.docx the server could not convert arrives in the format it was
      // generated in; say so rather than leave the changed extension unexplained.
      if (warning) globalThis.alert(warning);
    } catch (e) {
      globalThis.alert(e instanceof Error ? e.message : String(e));
    } finally {
      onBusy?.(false);
    }
    return;
  }
  const { save, message } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");

  const outputFile = await save({
    title: getTigerResource("TIGER_SAVE_REPORT_TITLE"),
    defaultPath: await pickerSavePath(PickerKey.controllerReport, `${configName}_tiger_controller_report.doc`),
    filters: toTauriFilters(CONTROLLER_REPORT_FORMATS),
  });
  if (!outputFile) return; // cancelled
  rememberPickerDir(PickerKey.controllerReport, outputFile);

  const title = getTigerResource("TIGER_REPORT_SUCCESS_TITLE");
  onBusy?.(true);
  try {
    await invoke("create_controller_report", {
      configName,
      outputFile,
      titleFormat: getTigerResource("TIGER_CONTROL_TITLE"),
      pageLabel: controllerPageLabel(),
    });
  } catch (e) {
    onBusy?.(false);
    await message(String(e), { title, kind: "error" });
    return;
  }
  onBusy?.(false);
  await offerToOpen(outputFile, title);
}

/** The localized strings the stability document bakes in (C++
 *  get_tiger_resource calls inside the writers). */
const STABILITY_RESOURCE_IDS = [
  "TIGER_PERF_TITLE",
  "TIGER_PERF_CURRENT",
  "TIGER_PERF_POWER",
  "TIGER_PERF_HEAT",
  "TIGER_PERF_COMP",
  "TIGER_PERF_INLET",
  "TIGER_PERF_WINDOW",
  "TIGER_PERF_DEV",
  "TIGER_PERF_RED",
  "TIGER_PERF_TAGNAME",
  "TIGER_PERF_DEVIATION",
  "TIGER_PERF_ALLOW",
  "TIGER_PERF_ANALYSIS",
  "TIGER_PERF_NOTBASE",
  "TIGER_PERF_TEXT",
] as const;

/** Performance Stability report: current performance figures + per-channel
 *  stability deviations from the performance server
 *  (C++ OnApp_stability_report_button → Ti_request_performance_stability). */
export async function generateStabilityReport(
  configName: string,
  onBusy?: (busy: boolean) => void,
): Promise<void> {
  if (!isTauri()) {
    const filename = await promptReportSave({
      defaultName: `${configName}_tiger_stability_report.doc`,
      formats: STABILITY_REPORT_FORMATS,
      rememberKey: PickerKey.stabilityReport,
    });
    if (!filename) return; // cancelled
    const resources = Object.fromEntries(
      STABILITY_RESOURCE_IDS.map((id) => [id, getTigerResource(id)]),
    );
    onBusy?.(true);
    try {
      const { warning } = await downloadReport("/api/create_stability_report", filename, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config_name: configName, filename, resources }),
      });
      // A .pdf/.docx the server could not convert arrives in the format it was
      // generated in; say so rather than leave the changed extension unexplained.
      if (warning) globalThis.alert(warning);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      globalThis.alert(msg === "TIGER_PERF_PROC" ? getTigerResource("TIGER_PERF_PROC") : msg);
    } finally {
      onBusy?.(false);
    }
    return;
  }
  const { save, message } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");

  const outputFile = await save({
    title: getTigerResource("TIGER_SAVE_REPORT_TITLE"),
    defaultPath: await pickerSavePath(PickerKey.stabilityReport, `${configName}_tiger_stability_report.doc`),
    filters: toTauriFilters(STABILITY_REPORT_FORMATS),
  });
  if (!outputFile) return; // cancelled
  rememberPickerDir(PickerKey.stabilityReport, outputFile);

  const title = getTigerResource("TIGER_REPORT_SUCCESS_TITLE");
  onBusy?.(true);
  try {
    const resources = Object.fromEntries(
      STABILITY_RESOURCE_IDS.map((id) => [id, getTigerResource(id)]),
    );
    await invoke("create_stability_report", { configName, outputFile, resources });
  } catch (e) {
    onBusy?.(false);
    // Backend marker for "the performance server didn't answer".
    const text =
      String(e) === "TIGER_PERF_PROC" ? getTigerResource("TIGER_PERF_PROC") : String(e);
    await message(text, { title, kind: "error" });
    return;
  }
  onBusy?.(false);
  await offerToOpen(outputFile, title);
}

/** Window > Save Tools as Report (C++ Ui_tool_win_save → create_tool_xml/
 *  fodt_report): every open graph window rendered offscreen and embedded with
 *  its channel list, diagnostics windows as their displayed lines. Saving as
 *  .xml/.fodt produces a report TEMPLATE carrying TGraph/TDiagnostics
 *  directives that create_xml_report can re-generate later. */
export async function generateToolReport(onBusy?: (busy: boolean) => void): Promise<void> {
  if (!isTauri()) {
    await generateToolReportWeb(onBusy);
    return;
  }
  const { save, message } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");

  // C++ defaults the name to "{site}_tiger_report".
  const site = await invoke<string>("get_site_config_name").catch(() => "");
  const outputFile = await save({
    title: getTigerResource("TIGER_SAVE_REPORT_TITLE"),
    defaultPath: await pickerSavePath(PickerKey.toolReport, `${site}_tiger_report.doc`),
    filters: toTauriFilters(TOOL_REPORT_FORMATS),
  });
  if (!outputFile) return; // cancelled
  rememberPickerDir(PickerKey.toolReport, outputFile);

  const title = getTigerResource("TIGER_REPORT_SUCCESS_TITLE");
  // The same snapshot broadcast Save Tool Set uses; an empty list still
  // produces a (bare) document, like the C++.
  const dialogs = await collectToolSetDialogs();
  onBusy?.(true);
  try {
    await invoke("create_tool_report", { outputFile, dialogs });
  } catch (e) {
    onBusy?.(false);
    await message(String(e), { title, kind: "error" });
    return;
  }
  onBusy?.(false);
  await offerToOpen(outputFile, title);
}

/** Web build of Save Tools as Report. The name + format come from
 *  ReportSaveDialog (a browser has no native save dialog), the open tool
 *  windows' descriptors from the BroadcastChannel snapshot round-trip; the POST
 *  to `/api/create_tool_report` renders the graphs/diagnostics through the
 *  headless worker and streams the document back as a browser download. */
export async function generateToolReportWeb(onBusy?: (busy: boolean) => void): Promise<void> {
  // C++ defaults the name to "{site}_tiger_report".
  const site = await fetch("/api/get_site_config_name")
    .then((r) => r.text())
    .then((t) => t.trim())
    .catch(() => "");
  const filename = await promptReportSave({
    defaultName: `${site ? `${site}_` : ""}tiger_report.doc`,
    formats: TOOL_REPORT_FORMATS,
    rememberKey: PickerKey.toolReport,
  });
  if (!filename) return; // cancelled

  // An empty list still produces a (bare) document, matching the C++.
  const dialogs = await collectToolSetDialogs();
  onBusy?.(true);
  try {
    const { warning } = await downloadReport("/api/create_tool_report", filename, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename, dialogs }),
    });
    // A .pdf/.docx the server could not convert arrives in the format it was
    // generated in; say so rather than leave the changed extension unexplained.
    if (warning) globalThis.alert(warning);
  } catch (e) {
    globalThis.alert(e instanceof Error ? e.message : String(e));
  } finally {
    onBusy?.(false);
  }
}

/** Event-model report: the model's event windows + per-tag values fetched
 *  from the alarm server, written tab-separated (.txt or .xls). */
export async function generateEventReport(
  configName: string,
  modelName: string,
  onBusy?: (busy: boolean) => void,
): Promise<void> {
  if (!isTauri()) {
    const filename = await promptReportSave({
      defaultName: `${configName}_tiger_event_report.txt`,
      formats: EVENT_REPORT_FORMATS,
      rememberKey: PickerKey.eventReport,
    });
    if (!filename) return; // cancelled
    const params = new URLSearchParams({
      config_name: configName,
      model_name: modelName,
      filename,
      title_format: getTigerResource("TIGER_EVENT_TITLE"),
      model_label: getTigerResource(modelName),
      start_label: getTigerResource("TIGER_RES_START_TIME"),
      end_label: getTigerResource("TIGER_RES_END_TIME"),
      duration_label: getTigerResource("TIGER_RES_DURATION"),
    });
    onBusy?.(true);
    try {
      await downloadReport(`/api/create_event_report?${params}`, filename);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      globalThis.alert(msg === "TIGER_EVENT_PROC" ? getTigerResource("TIGER_EVENT_PROC") : msg);
    } finally {
      onBusy?.(false);
    }
    return;
  }
  const { save, message } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");

  const outputFile = await save({
    title: getTigerResource("TIGER_SAVE_REPORT_TITLE"),
    defaultPath: await pickerSavePath(PickerKey.eventReport, `${configName}_tiger_event_report.txt`),
    filters: toTauriFilters(EVENT_REPORT_FORMATS),
  });
  if (!outputFile) return; // cancelled
  rememberPickerDir(PickerKey.eventReport, outputFile);

  const title = getTigerResource("TIGER_REPORT_SUCCESS_TITLE");
  onBusy?.(true);
  try {
    await invoke("create_event_report", {
      configName,
      modelName,
      outputFile,
      titleFormat: getTigerResource("TIGER_EVENT_TITLE"),
      // C++ titles with get_tiger_resource(model_string from the reply); the
      // menu's model name is what the server echoes back.
      modelLabel: getTigerResource(modelName),
      startLabel: getTigerResource("TIGER_RES_START_TIME"),
      endLabel: getTigerResource("TIGER_RES_END_TIME"),
      durationLabel: getTigerResource("TIGER_RES_DURATION"),
    });
  } catch (e) {
    onBusy?.(false);
    // Backend marker for "the event-model server didn't answer".
    const text =
      String(e) === "TIGER_EVENT_PROC" ? getTigerResource("TIGER_EVENT_PROC") : String(e);
    await message(text, { title, kind: "error" });
    return;
  }
  onBusy?.(false);
  await offerToOpen(outputFile, title);
}
